import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useAddTasting } from "@/api/hooks/useOutreach";
import { TASTING_KINDS, TASTING_KIND_LABELS, type TastingKind } from "@/api/types/outreach";

interface AddTastingDialogProps {
  open: boolean;
  onClose: () => void;
  outreachId: string;
}

/**
 * Logs a tasting offer (POST /v2/outreach/:id/tastings). credited_toward_booking
 * flags a tasting that later converts to a paid event, per the playbook's
 * tasting tracker.
 */
export function AddTastingDialog({ open, onClose, outreachId }: AddTastingDialogProps) {
  const [kind, setKind] = useState<TastingKind>("box");
  const [scheduledFor, setScheduledFor] = useState("");
  const [credited, setCredited] = useState(false);

  const addTasting = useAddTasting();

  useEffect(() => {
    if (open) {
      setKind("box");
      setScheduledFor("");
      setCredited(false);
    }
  }, [open]);

  async function handleSubmit() {
    try {
      await addTasting.mutateAsync({
        outreachId,
        kind,
        scheduled_for: scheduledFor || undefined,
        credited_toward_booking: credited,
      });
      toastSuccess("Tasting added");
      onClose();
    } catch (err) {
      toastError("Couldn't add tasting", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Add tasting">
      <DialogContent size="sm">
        <DialogHeader onClose={onClose}>Add tasting</DialogHeader>
        <DialogBody className="space-y-4">
          <FormSelect
            label="Kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as TastingKind)}
          >
            {TASTING_KINDS.map((k) => (
              <option key={k} value={k}>
                {TASTING_KIND_LABELS[k]}
              </option>
            ))}
          </FormSelect>

          <FormField
            label="Scheduled for (optional)"
            type="date"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
          />

          <Checkbox
            label="Credited toward a booking"
            checked={credited}
            onChange={(e) => setCredited(e.target.checked)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={addTasting.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={addTasting.isPending}>
            {addTasting.isPending ? "Saving..." : "Add tasting"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
